<template>
    <Modal :onClose="handleClose" padding="m" size="s">
        <div class="modal-box-content">
            <div class="modal-box-content-texts">
                <p class="title">
                    {{
                        t({
                            key: 'checkout.remove_product_type',
                            values: [productType],
                        })
                    }}
                </p>

                <p v-if="!combinationProducts.length" class="description">
                    {{
                        t({
                            key: 'checkout.do_you_want_remove_product_type_from_trip',
                            values: [product.Description],
                        })
                    }}
                </p>
                <div v-else class="note">
                    <p class="description">
                        {{
                            t({
                                key: 'shared.remove_additional_products',
                            })
                        }}
                    </p>
                    <Notification
                        :title="product.Description"
                        padding="l"
                        variant="warning"
                        :icon="getProductIcon(product.Type)" />

                    <Notification
                        v-for="cProduct in combinationProducts"
                        :key="cProduct.Id"
                        :title="cProduct.Description"
                        padding="l"
                        variant="warning"
                        :icon="getProductIcon(cProduct.Type)" />
                </div>

                <div v-if="connectedTransfers.length" class="note">
                    <p class="description">
                        {{
                            t({
                                key: 'shared.remove_connected_transfers',
                            })
                        }}
                    </p>

                    <Notification
                        v-for="cProduct in connectedTransfers"
                        :key="cProduct.Id"
                        :title="cProduct.Description"
                        variant="warning"
                        :icon="getProductIcon(cProduct.Type)" />
                </div>
            </div>
            <div class="modal-box-content-buttons">
                <ButtonPrimary
                    :loading="isDeleting"
                    :onClick="() => !isDeleting && handleDelete()">
                    {{ t({ key: 'shared.yes_discard' }) }}
                </ButtonPrimary>
                <ButtonText :disabled="isDeleting" :onClick="handleClose">
                    {{ t({ key: 'cancellation.cancel' }) }}
                </ButtonText>
            </div>
        </div>
    </Modal>
</template>

<script lang="ts">
    import { computed, defineComponent, PropType, ref } from 'vue';
    import { t } from '@shared/helpers/translation';
    import { MainProduct } from '@shared/interfaces/product';
    import {
        BonusNightProduct,
        HotelProduct,
        ProductType,
        TransferProduct,
    } from '@/api/Cart/cart.interfaces';
    import {
        deleteState,
        getSectionProductId,
    } from '@/components/checkoutPageV3/CheckoutV3.helpers';
    import { CheckoutStepState, CheckoutStepType } from '@/store/checkout/types';
    import { logAnalyticsAddRemoveProduct } from '@/tracking/analytics/Checkout/Functions/checkout.functions';
    import { EventName } from '@/tracking/analytics/types';
    import { logStatsigCheckoutRemoveProduct } from '@/tracking/statsig/checkout.functions';
    import { displayErrorModal, handleCustomError } from '@/config/errorHandling';
    import { trackClarity } from '@/tracking/clarity';
    import { ClarityEvent } from '@/tracking/clarity/types';
    import { CartProductCombination } from '@shared/interfaces/productCombination';
    import { useStore } from '@/store';
    import { isEmpty } from 'lodash';
    import { isNotNil } from '@shared/utils/value';
    import { getProductIcon } from '@/helpers/cart';
    import Modal from '@ui/components/Modal/Modal.vue';
    import ButtonPrimary from '@ui/components/Button/ButtonPrimary.vue';
    import ButtonText from '@ui/components/Button/ButtonText.vue';
    import Notification from '@ui/components/Notification/Notification.vue';

    interface StepData {
        state: CheckoutStepState;
        index: number;
    }

    export default defineComponent({
        name: 'DeleteProductModal',
        methods: { t },
        components: { ButtonPrimary, ButtonText, Modal, Notification },
        props: {
            product: {
                type: Object as PropType<MainProduct>,
                required: true,
            },
            onClose: Function,
        },
        setup(props) {
            const store = useStore();

            const isDeleting = ref<boolean>(false);

            const mainProducts = computed<MainProduct[]>(
                () => store.getters['cart/getMainProducts']
            );

            const connectedTransfers = computed<TransferProduct[]>(() => {
                const transfers: TransferProduct[] =
                    store.getters['cart/getTransferProducts'];

                if (!isEmpty(transfers)) {
                    const connected = transfers.filter(
                        (x) => x.TripId && x.TripId.split('.').includes(props.product.Id)
                    );

                    return connected;
                }

                return [];
            });

            const connectedBonusNightProducts = computed<BonusNightProduct[]>(() => {
                const hotels: HotelProduct[] = store.getters['cart/getHotelProducts'];

                if (hotels.length === 1) {
                    return store.getters['cart/getBonusNightProducts'] as BonusNightProduct[];
                }

                return [];
            });

            const combinationProducts = computed(() => {
                return mainProducts.value.filter(
                    (product) =>
                        isNotNil(props.product.CombinationId) &&
                        product.CombinationId === props.product.CombinationId &&
                        product.Id !== props.product.Id
                );
            });

            const productType = computed(() => {
                switch (props.product.Type) {
                    case ProductType.Hotel:
                        return t({ key: 'shared.accommodation_product' });
                    case ProductType.Flight:
                        return t({ key: 'shared.flight_product' });
                    case ProductType.Ferry:
                        return t({ key: 'shared.ferry_product' });
                    case ProductType.Activity:
                        return t({ key: 'shared.activity_product' });
                    case ProductType.Transfer:
                        return t({ key: 'shared.transfer_product' });
                    default:
                        return '';
                }
            });

            const handleClose = () => {
                props.onClose && props.onClose();
            };

            const handleDelete = () => {
                deleteProduct(props.product);
            };

            const getCurrentCheckoutStep = (product: MainProduct): StepData | undefined => {
                const states = store.getters['checkout/getStates'] as CheckoutStepState[];
                const productStepId = getSectionProductId(product.Id, product.Type);
                const index = states.findIndex((x) => x.id === productStepId);

                if (states[index]) {
                    return {
                        state: states[index],
                        index,
                    };
                }

                return undefined;
            };

            const deleteProduct = (product: MainProduct) => {
                try {
                    isDeleting.value = true;

                    const combinationsIds = combinationProducts.value.map((x) => x.Id);
                    const transfersIds = connectedTransfers.value.map((x) => x.Id);
                    const sembonusIds = connectedBonusNightProducts.value.map((x) => x.Id);
                    const step = getCurrentCheckoutStep(product);

                    store
                        .dispatch('cart/deleteProduct', {
                            product,
                            connectedIds: [
                                ...combinationsIds,
                                ...transfersIds,
                                ...sembonusIds,
                            ],
                        })
                        .then(() => {
                            logStatsigCheckoutRemoveProduct(product);
                            logAnalyticsAddRemoveProduct(
                                product,
                                EventName.CHECKOUT_REMOVE_PRODUCT
                            );

                            const servicePackageAddons =
                                store.getters['cart/getServicePackageAddons'];
                            if (servicePackageAddons.length === 0) {
                                deleteState(CheckoutStepType.ServicePackage);
                            }

                            const productCombination: CartProductCombination =
                                store.getters['cart/getProductCombination'];
                            if (
                                product.Type === ProductType.Hotel &&
                                !productCombination.Includes.Hotel
                            ) {
                                deleteState(CheckoutStepType.Sembonus);
                            }

                            if (step) {
                                deleteState(step.state.id);
                            }
                        })
                        .catch((error) => {
                            const message =
                                error && typeof error === 'string'
                                    ? error
                                    : t({
                                          key: 'shared.something_went_wrong',
                                      });

                            displayErrorModal(message, false);
                            trackClarity(ClarityEvent.CHECKOUT_REMOVE_PRODUCT_ERROR);
                        })
                        .finally(() => {
                            isDeleting.value = false;
                            handleClose();
                        });
                } catch (error) {
                    handleCustomError(`[DeleteProduct] deleteProduct: ${error}`);
                }
            };

            return {
                isDeleting,
                productType,
                combinationProducts,
                getProductIcon,
                connectedTransfers,
                handleClose,
                handleDelete,
            };
        },
    });
</script>
<style lang="scss" scoped>
    @forward 'DeleteProduct';
</style>
